/**
 * Stick a Pin In It — reusable opening prompt (offload / goal / skip).
 *
 * Usage:
 *   <StickAPinPrompt
 *     role="learner" | "instructor"  // drives copy + accent colour
 *     onSkip={() => void}            // optional, defaults to dismiss
 *     onPinned={(pin) => void}       // optional callback after save
 *   />
 *
 * Self-hides after skip or save. Shows a thin input-line when the user picks
 * Offload or Pin. Calm, clean — not a notes app.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pin, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  learner: {
    question: "Before we begin — is there anything you want to offload, or any goal you want to pin for today?",
    offloadBtn: "Offload something",
    goalBtn: "Pin today's goal",
    skipBtn: "Skip",
    offloadPlaceholder: "e.g. Feeling nervous about roundabouts",
    goalPlaceholder: "e.g. Today I'll stay calm at junctions",
  },
  instructor: {
    question: "Before we begin — is there any goal you want to make today, or anything you need to offload and remember later?",
    offloadBtn: "Offload a thought",
    goalBtn: "Pin a goal",
    skipBtn: "Skip",
    offloadPlaceholder: "e.g. Call garage about the brakes",
    goalPlaceholder: "e.g. Better debriefs today",
  },
};

export default function StickAPinPrompt({ role = "learner", onSkip, onPinned }) {
  const copy = COPY[role] || COPY.learner;
  const [dismissed, setDismissed] = useState(false);
  const [mode, setMode] = useState(null); // null | 'offload' | 'goal'
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  if (dismissed) return null;

  const handleSkip = () => {
    setDismissed(true);
    onSkip?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const clean = text.trim();
    if (!clean) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/pins`,
        { type: mode, text: clean },
        { withCredentials: true },
      );
      toast.success(
        mode === "goal" ? "Pinned for today" : "Offloaded — it's parked for you",
      );
      onPinned?.(data.pin);
      setDismissed(true);
    } catch {
      toast.error("Couldn't save your pin — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`pin-prompt-${role}`}
      className="bg-gradient-to-br from-indigo-50 to-slate-50 dark:from-indigo-900/15 dark:to-slate-900/30 border border-indigo-200/70 dark:border-indigo-900/50 rounded-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
          <Pin className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-400">
            Stick a pin in it
          </p>
          <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug mt-0.5">
            {copy.question}
          </p>

          {/* Action buttons */}
          {mode === null && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => setMode("offload")}
                data-testid="pin-action-offload"
                className="text-xs font-bold bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-lg px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
              >
                {copy.offloadBtn}
              </button>
              <button
                type="button"
                onClick={() => setMode("goal")}
                data-testid="pin-action-goal"
                className="text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700 transition-colors"
              >
                {copy.goalBtn}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                data-testid="pin-action-skip"
                className="text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg px-3 py-2 transition-colors"
              >
                {copy.skipBtn}
              </button>
            </div>
          )}

          {/* Input row */}
          {mode !== null && (
            <form onSubmit={handleSubmit} className="mt-3 flex items-center gap-2">
              <input
                type="text"
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={240}
                placeholder={mode === "goal" ? copy.goalPlaceholder : copy.offloadPlaceholder}
                data-testid="pin-input"
                className="flex-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <button
                type="submit"
                disabled={saving || !text.trim()}
                data-testid="pin-submit"
                className="text-xs font-bold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
              >
                Pin
              </button>
              <button
                type="button"
                onClick={() => { setMode(null); setText(""); }}
                data-testid="pin-cancel"
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
